import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { BaseChartComponent } from "./base-chart.component";
import { ChartConfiguration, Plugin } from "chart.js/auto";
import { AlertLevels, DataPointDto } from "src/app/services/api";
import { TranslateService } from "@ngx-translate/core";
import { PRIMARY_LINE_COLOR, SECONDARY_LINE_COLOR } from "src/app/constants/index";

@Component({
  selector: "app-water-level-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
})
export class WaterLevelChartComponent extends BaseChartComponent implements OnChanges {
  @Input() public alertLevels!: AlertLevels;
  @Input() public simulationData?: DataPointDto[];

  private observedDataLabel: string;
  private simulationDataLabel: string;

  constructor(translate: TranslateService, cdr: ChangeDetectorRef) {
    super(translate, cdr);
    this.observedDataLabel = translate.instant("STATION_PAGE.OBSERVED_LEVEL");
    this.simulationDataLabel = translate.instant("STATION_PAGE.PREDICTED_LEVEL");
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update the data property for app-empty-data component
    if (this.data?.length || this.simulationData?.length) {
      // Force chart re-rendering when data changes
      if ((changes.data || changes.simulationData) && this.chartContainer) {
        setTimeout(() => {
          this.renderChart();
          this.cdr.detectChanges();
        }, 0);
        return;
      }
    }

    // Call parent's ngOnChanges to ensure base component logic runs
    super.ngOnChanges(changes);
  }

  // Utiliza el plugin unificado de la clase base con el tipo "water"
  private drawWaterAlertLevelsPlugin = this.drawAlertLevelsPlugin("water");

  protected getChartConfig(): ChartConfiguration {
    const transformedData = this.data.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (point.value === null) {
        return { x: timestamp, y: null };
      } else {
        return { x: timestamp, y: Math.max(point.value / 100, 0) };
      }
    });

    const xSuggestedMax = !this.currentTime ? new Date().getTime() : undefined;
    const ySuggestedMax =
      this.alertLevels?.level3Centimeters / 100 ||
      this.alertLevels?.level2Centimeters / 100 ||
      this.alertLevels?.level1Centimeters / 100 ||
      2;

    // Get common chart options with meters as unit
    const commonOptions = this.getCommonChartOptions(xSuggestedMax, ySuggestedMax, " m");

    // If we have predicted data, show both datasets
    if (this.simulationData?.length) {
      this.showCurrentTimeLine = true;
      const transformedSimulationData = this.simulationData.map(point => {
        const timestamp = new Date(point.timestamp).getTime();
        if (point.value === null) {
          return { x: timestamp, y: null };
        } else {
          return { x: timestamp, y: Math.max(point.value / 100, 0) };
        }
      });

      return {
        type: "line",
        data: {
          datasets: [
            {
              data: transformedData,
              label: this.observedDataLabel,
              borderColor: PRIMARY_LINE_COLOR,
              pointBackgroundColor: PRIMARY_LINE_COLOR,
              backgroundColor: PRIMARY_LINE_COLOR,
              borderWidth: 1.5,
              pointRadius: 1,
              tension: 0.5,
              parsing: false,
            },
            {
              data: transformedSimulationData,
              label: this.simulationDataLabel,
              borderColor: SECONDARY_LINE_COLOR,
              pointBackgroundColor: SECONDARY_LINE_COLOR,
              backgroundColor: SECONDARY_LINE_COLOR,
              borderWidth: 1.5,
              pointRadius: 1,
              tension: 0.5,
              parsing: false,
            },
          ],
        },
        options: {
          ...commonOptions,
          plugins: {
            legend: {
              display: true,
              labels: {
                boxHeight: 2,
                boxWidth: 24,
              },
            },
          },
        },
        plugins: [this.legendSpacingPlugin, this.drawWaterAlertLevelsPlugin, this.drawCurrentTimeLinePlugin],
      };
    } else {
      // If no predicted data, maintain original behavior
      return {
        type: "line",
        data: {
          datasets: [
            {
              data: transformedData,
              label: this.observedDataLabel,
              borderColor: PRIMARY_LINE_COLOR,
              backgroundColor: PRIMARY_LINE_COLOR,
              borderWidth: 2,
              pointRadius: 1,
              tension: 0.5,
              parsing: false,
            },
          ],
        },
        options: {
          ...commonOptions,
          plugins: {
            legend: {
              display: true,
              labels: {
                boxHeight: 2,
                boxWidth: 24,
              },
            },
          },
        },
        plugins: [this.legendSpacingPlugin, this.drawWaterAlertLevelsPlugin],
      };
    }
  }
}
